import { createEffect, ofType, Actions } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { 
  catchError, 
  filter, 
  map, 
  mergeMap, 
  switchMap, 
  withLatestFrom 
} from 'rxjs/operators';
import { Action } from '@ngrx/store';

/**
 * Generic data fetching effect creator for time-series modules
 */
export function createTimeSeriesGroupFetchEffect<
  TPayload,
  TResponse,
  TRequest = any,
  TState = any
>(config: {
  groupActionType: any;
  apiCall: (request: TRequest) => Observable<TResponse>;
  existsCheck: (payload: TPayload, state: TState) => boolean;
  buildRequest: (payload: TPayload, clientId: string, receiverId?: string) => TRequest;
  successActionCreator: any;
  failActionCreator: any;
  cancelActionCreator: any;
  selectGroupedData: (state: any) => TState;
  activeClientId$: Observable<string>;
}) {
  return (actions$: Actions, store: Store) => createEffect(() =>
    actions$.pipe(
      ofType(config.groupActionType),
      withLatestFrom(store.pipe(select(config.selectGroupedData))),
      mergeMap(([action, groupedData]: [any, TState]) => {
        const exists = config.existsCheck(action.data, groupedData);

        if (exists && !action.data.isPeriodUpdate) {
          return of(config.cancelActionCreator());
        }

        return config.activeClientId$.pipe(
          switchMap((clientId) => {
            const request = config.buildRequest(action.data, clientId, action.receiverId);
            
            return config.apiCall(request).pipe(
              map(response => {
                const responseData = response && typeof response === 'object' ? response as { items?: any[] } : {};
                return config.successActionCreator({
                  items: responseData.items || [],
                  timeGroupBy: action.data.groupRange.timeGroupBy,
                  groupBy: action.data.groupBy,
                  limit: action.data.groupRange.limit,
                  from: action.data.groupRange.range?.from,
                  till: action.data.groupRange.range?.to,
                  key: action.data.groupRange.key,
                  projectId: action.data.projectId,
                  serviceId: action.data.serviceId,
                  metricId: action.data.metricId
                }, action);
              }),
              catchError(error => of(config.failActionCreator(error, action)))
            );
          })
        );
      })
    )
  );
}

/**
 * Generic auto-loading effect creator for time-series modules
 */
export function createTimeSeriesAutoLoadEffect(config: {
  activeClientId$: Observable<string>;
  defaultGroupRange: any;
  groupActionCreator: (payload: { data: any }) => Action;
  autoLoadConfigs: Array<{
    groupBy: string;
    groupRange: any;
    extras?: any;
  }>;
}) {
  return () => createEffect(() =>
    config.activeClientId$.pipe(
      filter(clientId => !!clientId),
      switchMap(() => 
        of(...config.autoLoadConfigs.map(autoConfig => 
          config.groupActionCreator({
            data: {
              groupRange: autoConfig.groupRange,  
              groupBy: autoConfig.groupBy,
              ...autoConfig.extras
            }
          })
        ))
      )
    )
  );
}

